import React, { useState, useEffect, useCallback } from "react";
import { Search, TrendingUp, Sparkles, Zap } from "lucide-react";
import axios from "axios";
import TrendCard from "@/components/TrendCard";
import CategoryFilter from "@/components/CategoryFilter";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";
import { useAuth } from "@/App";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const { user } = useAuth();

  const [trends, setTrends] = useState([]);
  const [hotTrends, setHotTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [savedTrends, setSavedTrends] = useState(new Set());
  const [isPersonalized, setIsPersonalized] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [fetchKey, setFetchKey] = useState(0);
  const [nicheOnly, setNicheOnly] = useState(false);
  const [feedMode, setFeedMode] = useState("standard");
  const [discoveryCount, setDiscoveryCount] = useState(0);

  const [localUser, setLocalUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Load local user from storage
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        setLocalUser(JSON.parse(storedUser));
      } catch (e) {
        console.error("Failed to parse stored user:", e);
      }
    }
    setAuthChecked(true);
  }, []);

  const effectiveUser = user || localUser;
  const isPro =
    effectiveUser?.user_tier === "pro" || effectiveUser?.is_premium;

  // Fetch trends (standard or personalized)
  const fetchTrends = useCallback(async () => {
    setTrends([]);
    setLoading(true);
    setIsPersonalized(false);
    setUserProfile(null);
    setFeedMode("standard");
    setDiscoveryCount(0);

    const token = localStorage.getItem("token");
    const timestamp = Date.now();

    try {
      if (isPro && token) {
        const params = new URLSearchParams();
        if (selectedCategory !== "All") params.append("category", selectedCategory);
        if (searchQuery) params.append("search", searchQuery);
        params.append("limit", "12");
        params.append("niche_only", nicheOnly.toString());
        params.append("_t", timestamp.toString());

        const response = await axios.get(
          `${API}/feed/personalized?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
            },
          }
        );

        const newTrends = Array.isArray(response.data.trends)
          ? response.data.trends
          : [];

        setFeedMode(response.data.mode || "personalized");
        setDiscoveryCount(response.data.discovery_count || 0);
        setTrends(newTrends);
        setIsPersonalized(response.data.personalized || false);
        setUserProfile(response.data.user_profile || null);
      } else {
        const params = new URLSearchParams();
        if (selectedCategory !== "All") params.append("category", selectedCategory);
        if (searchQuery) params.append("search", searchQuery);
        params.append("_t", timestamp.toString());

        const response = await axios.get(
          `${API}/trends?${params.toString()}`,
          {
            headers: {
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
            },
          }
        );

        const newTrends = Array.isArray(response.data)
          ? response.data
          : [];

        setTrends(newTrends);
        setIsPersonalized(false);
        setUserProfile(null);
      }
    } catch (error) {
      console.error("Error fetching trends:", error);
      try {
        const params = new URLSearchParams();
        if (selectedCategory !== "All") params.append("category", selectedCategory);
        params.append("_t", timestamp.toString());

        const response = await axios.get(`${API}/trends?${params.toString()}`);
        const fallbackTrends = Array.isArray(response.data)
          ? response.data
          : [];
        setTrends(fallbackTrends);
      } catch (fallbackError) {
        console.error("Fallback fetch failed:", fallbackError);
        setTrends([]);
      }
    } finally {
      setLoading(false);
    }
  }, [isPro, selectedCategory, searchQuery, nicheOnly]);

  // Fetch hot trends (currently unused but kept for future use)
  const fetchHotTrends = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/trends/hot?limit=5`);
      setHotTrends(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching hot trends:", error);
    }
  }, []);

  // Fetch saved trends
  const fetchSavedTrends = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await axios.get(`${API}/user/saved-trends`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const saved = new Set(
        (response.data.saved_trends || []).map((s) => s.trend.trend_id)
      );
      setSavedTrends(saved);
    } catch (error) {
      console.error("Error fetching saved trends:", error);
    }
  }, []);

  // Trigger fetches
  useEffect(() => {
    fetchTrends();
    fetchHotTrends();
    if (effectiveUser) fetchSavedTrends();
  }, [
    selectedCategory,
    effectiveUser?.user_id,
    effectiveUser?.user_tier,
    isPro,
    fetchKey,
    nicheOnly,
    fetchTrends,
    fetchHotTrends,
    fetchSavedTrends,
  ]);

  // Force refetch on login
  useEffect(() => {
    if (effectiveUser) {
      setFetchKey((prev) => prev + 1);
    }
  }, [effectiveUser?.user_id]);

  const handleSave = async (trendId) => {
    if (!effectiveUser) {
      toast.error("Please sign in to save trends");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/trends/${trendId}/save`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.saved) {
        setSavedTrends((prev) => new Set([...prev, trendId]));
        toast.success("Trend saved!");
      } else {
        setSavedTrends((prev) => {
          const next = new Set(prev);
          next.delete(trendId);
          return next;
        });
        toast.success("Trend removed");
      }
    } catch (error) {
      toast.error("Failed to save trend");
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTrends();
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="text-gray-500 text-sm">Loading...</span>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Search Bar */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="relative max-w-xl mx-auto">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            type="text"
            placeholder="Search trends, products, tools, hashtags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 pr-4 py-3 border-2 border-gray-300 focus:border-purple-600 bg-white text-sm shadow-sm hover:shadow transition-all"
          />
        </div>
      </form>

      {/* Category Filter */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-purple-600" />
          <h2 className="text-xs font-bold uppercase tracking-wide text-gray-700">
            Browse Categories
          </h2>
        </div>
        <CategoryFilter
          selected={selectedCategory}
          onSelect={setSelectedCategory}
        />
      </div>

      {/* Results Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-600" />
            <span className="font-bold uppercase tracking-wide text-sm text-gray-800">
              {selectedCategory === "All" ? "All Trends" : selectedCategory}
            </span>
          </div>
          <span className="text-sm text-gray-500 font-medium">
            ({Array.isArray(trends) ? trends.length : 0})
          </span>

          {discoveryCount > 0 && (
            <motion.span
              className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full font-bold border border-emerald-200"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
            >
              +{discoveryCount} discovery
            </motion.span>
          )}
        </div>

        {/* Niche Toggle */}
        {isPro && userProfile?.has_strong_preferences && (
          <div className="flex items-center gap-2 bg-gray-100 rounded-full p-1">
            <button
              onClick={() => setNicheOnly(false)}
              className={`px-3 py-1 text-xs font-medium rounded-full ${
                !nicheOnly
                  ? "bg-white text-black shadow-sm"
                  : "text-gray-500"
              }`}
            >
              Show All
            </button>
            <button
              onClick={() => setNicheOnly(true)}
              className={`px-3 py-1 text-xs font-medium rounded-full ${
                nicheOnly
                  ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-sm"
                  : "text-gray-500"
              }`}
            >
              My Niche Only
            </button>
          </div>
        )}
      </div>

      {/* Personalized Indicator */}
      {isPersonalized && userProfile && (
        <div className="flex items-center gap-2 mb-6 px-3 py-2 bg-purple-50 rounded-lg border border-purple-200">
          <Zap className="w-4 h-4 text-purple-600" />
          <span className="text-xs font-medium text-purple-700">
            {feedMode === "niche" ? "Niche Mode" : "Personalized Feed"}
          </span>
          {userProfile.top_categories?.length > 0 && (
            <span className="text-xs text-purple-500">
              • {userProfile.top_categories.slice(0, 2).join(", ")}
            </span>
          )}
        </div>
      )}

      {/* Feed */}
      {loading ? (
        <div className="columns-1 md:columns-2 lg:columns-3 gap-5 space-y-5">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="break-inside-avoid-column">
              <div className="bg-white/50 border border-gray-100 rounded-2xl shadow-sm h-80 animate-pulse opacity-40" />
            </div>
          ))}
        </div>
      ) : Array.isArray(trends) && trends.length > 0 ? (
        <>
          <div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4">
            {trends.map((trend, index) => {
              // Use category/tags instead of topics for card shape
              const primaryCategory =
                trend.category ||
                (Array.isArray(trend.tags) ? trend.tags[0] : "General");

              let cardShape = "medium";
              if (
                [
                  "Amazon Trending",
                  "TikTok Shop",
                  "Gadgets",
                  "Beauty",
                  "Home",
                  "Pets",
                  "Food",
                  "Fashion",
                  "Fitness",
                  "Viral Formats",
                ].includes(primaryCategory || "General")
              ) {
                cardShape = "tall";
              } else if (["Hashtags"].includes(primaryCategory || "General")) {
                cardShape = "short";
              }

              const breakInterval = 10;
              const needsSectionBreak =
                index > 0 && index % breakInterval === 0;

              const sectionLabels = [
                "✨ For You",
                "🔥 Trending Now",
                "📈 Rising Fast",
                "🎨 Creator Picks",
                "💡 Popular This Hour",
                "🚀 Hot Products",
              ];

              const sectionHeader =
                sectionLabels[
                  Math.floor(index / breakInterval) % sectionLabels.length
                ];

              return (
                <React.Fragment key={`trend-${trend.trend_id}`}>
                  {needsSectionBreak && (
                    <div className="break-inside-avoid-column mt-6 mb-4">
                      <motion.div
                        className="text-center py-3"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                      >
                        <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-1">
                          {sectionHeader}
                        </h3>
                        <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-purple-400 to-transparent mx-auto" />
                      </motion.div>
                    </div>
                  )}

                  <div className="break-inside-avoid-column mb-4">
                    <TrendCard
                      trend={trend}
                      index={index}
                      onSave={handleSave}
                      isSaved={savedTrends.has(trend.trend_id)}
                      isPro={isPro}
                      onUpgradeClick={() =>
                        (window.location.href = "/subscribe")
                      }
                      cardShape={cardShape}
                    />
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {/* Data Source Indicator */}
          <div className="mt-6 flex justify-center">
            <div
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-mono ${
                feedMode === "niche"
                  ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white"
                  : isPersonalized
                  ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white"
                  : "bg-gray-200 text-gray-600"
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full ${
                  isPersonalized ? "bg-green-400 animate-pulse" : "bg-gray-400"
                }`}
              ></span>
              <span>
                {feedMode === "niche"
                  ? "NICHE MODE"
                  : isPersonalized
                  ? "PERSONALIZED"
                  : "STANDARD"}
              </span>

              {isPersonalized && userProfile && (
                <span className="text-purple-200">
                  | Top: {userProfile.top_categories?.slice(0, 2).join(", ")}
                </span>
              )}

              {discoveryCount > 0 && (
                <span className="text-emerald-300">
                  | {discoveryCount} discovery items
                </span>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-gray-100 mx-auto mb-4 flex items-center justify-center border-2 border-black">
            <Search className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-xl font-bold mb-2">No trends found</h3>
          <p className="text-gray-500">
            Try adjusting your search or category filters
          </p>
        </div>
      )}
    </div>
  );
};

export default HomePage;